import React, { useEffect } from 'react'
import {
  graphql,
  HeadProps,
  navigate,
  PageProps,
  StaticQueryDocument,
} from 'gatsby'
import { GatsbyImage } from 'gatsby-plugin-image'

import { withPrismicPreview } from 'gatsby-plugin-prismic-previews'

import { PrismicRichText } from '@prismicio/react'

import BertholdTitle from 'src/components/atoms/bertholdTitle'
import { WithContext, Article } from 'schema-dts'
import DocumentHead from 'src/components/pageLayout/documentHead'

import {
  NewsroomArticleQuery,
  PrismicNewsroomArticle,
  PrismicSiteInformation,
  Site,
} from 'src/typings/generated/graphql'

// import { CircleFadeLoader } from 'react-loaders-kit'

import CustomLink from 'src/utils/customLink'

import WhiteWave from 'src/images/svgs/WhiteWave.svg'
import Eyebrow from 'src/images/svgs/Eyebrow.svg'
import TwitterIcon from 'src/images/svgs/icons/Xicon.svg'
import FacebookIcon from 'src/images/svgs/icons/FacebookIcon.svg'
import LinkedInIcon from 'src/images/svgs/icons/LinkedInIcon.svg'

import { articleBannerImageUrl } from 'src/utils/articlesHelper'
import NewsroomArticlesLibrary from 'src/components/organisms/engagement/newsroomArticlesLibrary'

import * as Styles from './{prismicNewsroomArticle.url}.module.scss'

const NewsroomArticleTemplate = ({ data }: PageProps<NewsroomArticleQuery>) => {
  if (!data) return null

  const article = data.prismicNewsroomArticle as PrismicNewsroomArticle

  const shareUrl = `https://raisingcanes.com${article.url}`
  const redirectUrl = article.data.url?.url

  useEffect(() => {
    if (redirectUrl && typeof window !== 'undefined') {
      navigate(redirectUrl)
    }
  }, [redirectUrl])

  const publishedDate = () => {
    const locale = process.env.PRISMIC_LOCALE || 'en-us'
    const dateStr = article.data.post_date ?? article.first_publication_date
    const date = new Date(dateStr)

    return date
      .toLocaleDateString(locale, {
        year: '2-digit',
        month: '2-digit',
        day: '2-digit',
      })
      .replaceAll('/', '.')
  }

  const hero = () => {
    if (article.data.banner_image?.gatsbyImageData) {
      return (
        <div className={`${Styles.hero} ${Styles.banner}`}>
          <div className={Styles.imageWrapper}>
            <GatsbyImage
              className={Styles.image}
              image={article.data.banner_image?.gatsbyImageData}
              alt={article.data.banner_image?.alt ?? ''}
            />
            <div className={Styles.imageOverlay} />
          </div>
          <div className={Styles.headline}>
            <div className={Styles.eyebrow}>
              <Eyebrow />
            </div>
            <h1>
              {article.data.title?.text &&
                BertholdTitle({ text: article.data.title?.text })}
            </h1>
            <p>{publishedDate()}</p>
          </div>
          <div className={Styles.wave}>
            <WhiteWave />
          </div>
        </div>
      )
    }

    return (
      <div className={Styles.hero}>
        <div className={Styles.headline}>
          <div className={Styles.eyebrow}>
            <Eyebrow />
          </div>
          <h1>{article.data.title?.text ?? ''}</h1>
          <p>{publishedDate()}</p>
        </div>
      </div>
    )
  }

  return (
    <div data-testid="newsroom-article-template">
      {redirectUrl && (
        <div className={Styles.loader}>
          {/* TODO: do we replace loader? */}
          {/* <CircleFadeLoader loading color="#d71c2e" /> */}
        </div>
      )}
      {!redirectUrl && (
        <div className={Styles.article}>
          {hero()}
          {article.data.url?.url && <p>{article.data.url?.url}</p>}
          <div className={Styles.bodyContainer}>
            <div className={Styles.body}>
              {article.data.outlet && <h4>{article.data.outlet}</h4>}
              <PrismicRichText
                field={article.data.body?.richText}
                components={{
                  hyperlink: ({ node, children }: any) =>
                    CustomLink({ link: node.data, children }),
                }}
              />
              <div className={Styles.social}>
                <p>Share:</p>
                <a
                  href={`http://twitter.com/intent/tweet/?url=${shareUrl}`}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="share on twitter"
                >
                  <TwitterIcon />
                </a>
                <a
                  href={`http://www.facebook.com/share.php?u=${shareUrl}`}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="share on facebook"
                >
                  <FacebookIcon />
                </a>
                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="share on linkedin"
                >
                  <LinkedInIcon />
                </a>
              </div>
            </div>
          </div>
          <NewsroomArticlesLibrary
            content={{
              headline: 'Recent Articles',
              articles: [],
              skipArticleId: article.uid,
            }}
          />
        </div>
      )}
    </div>
  )
}

export default withPrismicPreview(NewsroomArticleTemplate)

export const Head = ({ data }: HeadProps<NewsroomArticleQuery>) => {
  const { prismicNewsroomArticle, prismicSiteInformation, site } = data

  const {
    data: pageData,
    url,
    first_publication_date: firstPublicationDate,
    last_publication_date: lastPublicationDate,
    tags,
  } = prismicNewsroomArticle as PrismicNewsroomArticle

  const { data: siteInformationData } =
    prismicSiteInformation as PrismicSiteInformation

  const { siteMetadata } = site as Site

  const siteTitle =
    siteInformationData.site_name ?? siteMetadata?.title ?? 'Newsroom Article'
  const title = pageData.meta_title ?? pageData.title?.text ?? siteTitle

  const description = pageData.meta_description ?? ''

  const siteImageUrl = siteInformationData.site_icon?.url ?? ''
  const imageUrl =
    articleBannerImageUrl(prismicNewsroomArticle as PrismicNewsroomArticle) ??
    siteImageUrl
  const noIndex = pageData.meta_no_index ?? undefined

  const schemaData = {
    author: pageData.author ?? '',
    datePublished: pageData.post_date ?? firstPublicationDate ?? '',
    dateModified: lastPublicationDate ?? '',
  }

  const schema: WithContext<Article> = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    author: {
      '@type': 'Person',
      name: schemaData?.author ?? '',
    },
    copyrightHolder: {
      '@type': 'Organization',
      name: 'Raising Restaurants, LLC',
    },
    copyrightYear: schemaData?.datePublished.substring(0, 4),
    creator: {
      '@type': 'Person',
      name: schemaData?.author ?? '',
    },
    publisher: {
      '@type': 'Organization',
      name: siteTitle,
      logo: {
        '@type': 'ImageObject',
        url: siteImageUrl,
      },
    },
    datePublished: schemaData?.datePublished,
    dateModified: schemaData?.dateModified,
    description,
    headline: title,
    inLanguage: 'en',
    name: title,
    image: {
      '@type': 'ImageObject',
      url: imageUrl,
    },
    keywords: tags,
  }

  return DocumentHead({
    title,
    url: url ?? '',
    description: '',
    imageUrl: '',
    pageSchema: schema,
    noIndex,
  })
}

export const query: StaticQueryDocument = graphql`
  query NewsroomArticle($id: String) {
    prismicNewsroomArticle(id: { eq: $id }) {
      _previewable
      uid
      data {
        title {
          text
        }
        outlet
        body {
          richText
        }
        banner_image {
          gatsbyImageData
          alt
        }
        url {
          url
        }
        author
        post_date
        meta_title
        meta_description
        meta_image {
          url
        }
        meta_no_index
      }
      url
      first_publication_date
      tags
    }
    site {
      siteMetadata {
        title
        description
        url
        author
      }
    }
    prismicSiteInformation {
      data {
        site_name
        site_description
        site_icon {
          url
        }
      }
    }
  }
`
